/**
 * Générateur — Automatisme : reconnaître une situation de proportionnalité
 * Attendu ID : 1084 | Niveau : 5ème | Domaine : Proportionnalité
 * Rubrique : AUTOMATISME (programme 2025) — mobilisation en rituel court.
 * Labels (cartes exerciseur) :
 *   - Proportionnel ou pas ?             (V/F situations verbales)
 *   - Le tableau de prix                 (QCM oui/non justifié, données chiffrées)
 *   - Repérer la situation               (QCM + variante intrus « N'EST PAS »)
 *   - Proportionnalité : toujours vrai ? (V/F logique, cadence raisonnement)
 *
 * ── Plan de cartes validé (2026-08-31) ─────────────────────────────────────
 * Source : Automatisme n°46 du doc 5e + grille P1-P12 (analyse BO cycles 3-4).
 * LIMITE DE PÉRIMÈTRE : la proportionnalité se joue « exclusivement dans le
 * cadre des grandeurs » (6e) — toutes les situations sont contextualisées,
 * jamais de suite numérique abstraite. PROSCRIPTIONS (P7) : produit en croix
 * nulle part, même en distracteur ; aucune lettre, aucune formule.
 * INVARIANTS :
 *   - le statut (proportionnel ou non) des tableaux chiffrés est FIXÉ par
 *     construction puis RE-VÉRIFIÉ par produits croisés ENTIERS en centimes
 *     (vérification interne, jamais montrée) — throw si contradiction ;
 *   - l'obstacle P11 est servi à chaque tirage V/F : au moins une situation
 *     qui CROÎT sans être proportionnelle (aire du carré, part fixe,
 *     tarif dégressif) — « ça augmente ensemble » n'est pas un critère ;
 *   - QCM repérer : exactement UN candidat au statut demandé (recompté,
 *     throw sinon) ; variante intrus « N'EST PAS » en majuscules +
 *     contenu.negated = true (convention gen_1039) ;
 *   - QCM tableau : 4 réponses en deux paires Oui/Non de même gabarit, les
 *     deux pièges (« ça augmente donc oui », « pas entiers donc non ») ne
 *     sont JAMAIS corrects — ils matérialisent les misconceptions P2/P11 ;
 *   - prix à 2 décimales affichées (0,90 €), quantités simples (P3) ;
 *   - V/F logique : pool FIXE à vérité fixe, contenu.cadence = 'raisonnement'
 *     (métadonnée dormante, convention gen_1039).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeClasser,
} from '../../../utils';

const ATTENDU_ID = 1084;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_SITUATIONS = 'Proportionnel ou pas ?';
const L_TABLEAU = 'Le tableau de prix';
const L_REPERER = 'Repérer la situation';
const L_LOGIQUE = 'Proportionnalité : toujours vrai ?';

// ─── Le domaine : situations verbales par familles ─────────────────────────
// Chaque famille porte UNE vérité fixe (prop: true/false) et `croit: true` si
// les deux grandeurs augmentent ensemble — c'est le vivier de l'obstacle P11.
// Vocabulaire strictement cycle 3/6e : prix au kilo, à l'unité, allure
// constante (appuis explicites de la fiche n°46).

const SITUATIONS_PROP = [
    { fam: 'kilo', croit: true, variantes: [
        'Le prix payé et la masse de tomates achetées (prix au kilo fixe)',
        'Le prix payé et la masse de pommes achetées (prix au kilo fixe)',
        'Le prix payé et la masse de cerises achetées (prix au kilo fixe)',
    ] },
    { fam: 'unite', croit: true, variantes: [
        'Le prix payé et le nombre de photocopies (tarif unique à la copie)',
        'Le prix payé et le nombre de tickets de bus achetés (prix unique du ticket)',
        'Le prix payé et le nombre de cahiers identiques achetés',
    ] },
    { fam: 'perimetre', croit: true, variantes: [
        'Le périmètre d\'un carré et la longueur de son côté',
        'Le périmètre d\'un triangle équilatéral et la longueur de son côté',
    ] },
    { fam: 'vitesse', croit: true, variantes: [
        'La distance parcourue et la durée du trajet (allure constante)',
        'La distance parcourue à vélo et la durée du trajet (vitesse constante)',
    ] },
    { fam: 'parts', croit: true, variantes: [
        'Le nombre de parts obtenues et le nombre de pizzas identiques découpées',
        'Le nombre de parts obtenues et le nombre de tartes identiques découpées',
    ] },
    { fam: 'recette', croit: true, variantes: [
        'La quantité de farine utilisée et le nombre de crêpes d\'une même recette',
        'La quantité de lait utilisée et le nombre de gâteaux d\'une même recette',
    ] },
];

const SITUATIONS_NON_PROP = [
    { fam: 'age', croit: false, variantes: [
        'L\'âge d\'une personne et sa taille',
        'L\'âge d\'un enfant et sa pointure',
    ] },
    { fam: 'aire', croit: true, variantes: [
        'L\'aire d\'un carré et la longueur de son côté',
        // Enrichi (revue 2026-09-01) : même obstacle — doubler l\'arête
        // multiplie le volume par 8, pas par 2. Le cube est connu en 6e (P9).
        'Le volume d\'un cube et la longueur de son arête',
    ] },
    { fam: 'forfait', croit: true, variantes: [
        'Le prix d\'un abonnement (10 € fixes + 2 € par séance) et le nombre de séances',
        'Le prix payé (5 € de frais fixes + 3 € par livre) et le nombre de livres commandés',
    ] },
    { fam: 'degressif', croit: true, variantes: [
        // Lexique 5e (P8, revue 2026-08-31) : « dégressif » n'est pas exigible —
        // la baisse du prix par personne est dite avec des mots d'élève.
        'Le prix total d\'une sortie et le nombre de participants, quand le prix par personne baisse pour les grands groupes',
        'Le prix total des places de cinéma et le nombre de places, quand le prix par place baisse à partir de 5 places',
    ] },
    { fam: 'meteo', croit: false, variantes: [
        'La température extérieure et l\'heure de la journée',
        // Lexique 5e (P8, revue 2026-09-01) : « altitude » relève de la
        // géographie, pas du lexique mathématique exigible — mots de classe.
        'La masse d\'un cartable et le nombre d\'élèves de la classe',
    ] },
];

/** Tire une situation dans un vivier, familles déjà vues exclues. */
const tirerSituation = (vivier, famillesVues) => {
    const dispo = vivier.filter((s) => !famillesVues.has(s.fam));
    if (dispo.length === 0) return null;
    const s = randomChoice(dispo);
    famillesVues.add(s.fam);
    return { ...s, texte: randomChoice(s.variantes) };
};

// ─── Format 1 : V/F situations (le cœur de la série flash) ─────────────────

// Anti-répétition immédiate (recette standard).
let derniereSignatureSituations1084 = null;

const genSituationsVf = () => {
    let payload = construireSituationsVf();
    for (let t = 0; t < 8 && JSON.stringify(payload.contenu) === derniereSignatureSituations1084; t++) {
        payload = construireSituationsVf();
    }
    derniereSignatureSituations1084 = JSON.stringify(payload.contenu);
    return payload;
};

/**
 * Lot commun de 4 situations (V/F et classer) : obstacle P11 imposé, familles
 * uniques, au moins 2 proportionnelles, mélange final.
 */
const tirerLotSituations = () => {
    const famillesVues = new Set();
    const items = [];

    // Obstacle P11 imposé : une situation qui CROÎT sans être proportionnelle.
    const pieges = SITUATIONS_NON_PROP.filter((s) => s.croit);
    const piege = randomChoice(pieges);
    famillesVues.add(piege.fam);
    items.push({ ...piege, texte: randomChoice(piege.variantes), prop: false });

    // Compléter à 4 lignes : au moins 1 proportionnelle garantie, mélange libre.
    // Quota élargi (revue 2026-09-01, A2) : 1 à 3 proportionnelles — le nombre
    // de « pas proportionnel » varie de 1 à 3, l'élève ne peut plus déduire la
    // dernière ligne d'un méta-fait de quota.
    const nbProp = randInt(1, 3);
    for (let k = 0; k < nbProp; k++) {
        const s = tirerSituation(SITUATIONS_PROP, famillesVues);
        if (s) items.push({ ...s, prop: true });
    }
    while (items.length < 4) {
        const s = tirerSituation(SITUATIONS_NON_PROP, famillesVues);
        if (!s) break;
        items.push({ ...s, prop: false });
    }
    if (items.length < 4) throw new Error('gen_1084 : viviers insuffisants pour 4 lignes');
    return shuffle(items);
};

const construireSituationsVf = () => {
    const melange = tirerLotSituations();
    const payload = makeVraiFaux(
        melange.map((it) => it.texte),
        melange.map((it) => (it.prop ? 0 : 1)),
        '<p>Pour chaque situation, dis si les deux grandeurs sont proportionnelles ou non.</p>',
        { ...opts(L_SITUATIONS), colonnes: ['Proportionnel', 'Pas proportionnel'] },
    );
    payload._meta.params = { familles: melange.map((it) => it.fam) };
    return payload;
};

// ─── Format 1 bis : classer (mêmes lots, interaction glisser) ─────────────

// Anti-répétition immédiate (recette standard).
let derniereSignatureClasser1084 = null;

const genSituationsClasser = () => {
    let payload = construireSituationsClasser();
    for (let t = 0; t < 8 && payload.contenu.bonne_reponse === derniereSignatureClasser1084; t++) {
        payload = construireSituationsClasser();
    }
    derniereSignatureClasser1084 = payload.contenu.bonne_reponse;
    return payload;
};

const construireSituationsClasser = () => {
    const melange = tirerLotSituations();
    const payload = makeClasser(
        [
            { name: 'Proportionnel', items: melange.filter((it) => it.prop).map((it) => it.texte) },
            { name: 'Pas proportionnel', items: melange.filter((it) => !it.prop).map((it) => it.texte) },
        ],
        '<p>Glisse chaque situation dans la bonne colonne.</p>',
        opts(L_SITUATIONS),
    );
    payload._meta.params = { familles: melange.map((it) => it.fam) };
    return payload;
};

// ─── Format 2 : QCM tableau de prix (l'exercice d'application) ─────────────

const ARTICLES_1084 = [
    { plu: 'croissants', sing: 'croissant' },
    { plu: 'pains au chocolat', sing: 'pain au chocolat' },
    { plu: 'cahiers', sing: 'cahier' },
    { plu: 'tickets', sing: 'ticket' },
    { plu: 'sandwichs', sing: 'sandwich' },
    { plu: 'magazines', sing: 'magazine' },
];

// CHAÎNE DE DOUBLEMENTS (revue 2026-09-01) : q2 = 2·q1 ET q3 = 2·q2 — la
// confirmation complète du cas proportionnel tient en DEUX gestes triviaux
// (doubler, redoubler), jamais une division difficile (le 19,95 ÷ 7 relevé
// par le prof). La réfutation reste en UN geste : la casse est sur (q1, q2).
const TRIPLES_QTES = [[2, 4, 8], [3, 6, 12], [5, 10, 20]];

/** Centimes → écriture monétaire à 2 décimales (« 4,50 »). */
const eur = (c) => `${Math.floor(c / 100)},${String(c % 100).padStart(2, '0')}`;

/**
 * AUTO-VÉRIFICATION (P7-safe : interne, jamais montrée) : la proportionnalité
 * réelle des couples (q, prix en centimes) est recomptée par produits croisés
 * ENTIERS — throw si elle contredit le statut construit.
 */
const verifierProportionnalite = (couples, attendu) => {
    let prop = true;
    for (let i = 0; i < couples.length; i++) {
        for (let j = i + 1; j < couples.length; j++) {
            if (couples[i][1] * couples[j][0] !== couples[j][1] * couples[i][0]) prop = false;
        }
    }
    if (prop !== attendu) {
        throw new Error(`gen_1084 : tableau ${JSON.stringify(couples)} recompté ${prop}, attendu ${attendu}`);
    }
    return couples;
};

/** Construit les couples (quantité, prix en centimes) selon le statut voulu. */
const construireCouples = (prop) => {
    const qtes = randomChoice(TRIPLES_QTES);
    // Prix unitaire à 1-2 décimales « propres » : multiples de 5 centimes (P3).
    const unit = randInt(11, 69) * 5; // 0,55 € → 3,45 €
    if (prop) {
        return verifierProportionnalite(qtes.map((q) => [q, q * unit]), true);
    }
    // Cas non proportionnel : la CASSE est TOUJOURS sur la paire double
    // (q2 = 2 × q1) — le contrôle en un geste la détecte à coup sûr.
    if (Math.random() < 0.5) {
        // Part fixe : f + unit × q — le double de q1 ne double pas le prix.
        const fixe = randInt(4, 20) * 50; // 2,00 € → 10,00 €
        return verifierProportionnalite(qtes.map((q) => [q, fixe + q * unit]), false);
    }
    // Dégressif : q2 (le double) et q3 paient moins cher l'unité que q1.
    // Écart minimal de 1,00 € au doublement (revue 2026-09-01) : un écart de
    // 20 centimes ressemble à une erreur de calcul, pas à un signal — la
    // baisse est bornée par 2·q1·baisse ≥ 100 centimes.
    const q1 = qtes[0];
    const baisseMin = Math.ceil(100 / (2 * q1) / 5) * 5;
    const baisseMax = Math.min(8 * 5, Math.floor((unit - 30) / 2 / 5) * 5);
    if (baisseMax < baisseMin) {
        const fixe = randInt(4, 20) * 50;
        return verifierProportionnalite(qtes.map((q) => [q, fixe + q * unit]), false);
    }
    const baisse = randInt(baisseMin / 5, baisseMax / 5) * 5;
    const unites = [unit, unit - baisse, unit - 2 * baisse];
    return verifierProportionnalite(qtes.map((q, i) => [q, q * unites[i]]), false);
};

/**
 * AUTO-VÉRIFICATION du contrat « une étape » : q2 est le double de q1, et
 * dans les cas non proportionnels le prix de q2 diffère du double du prix
 * de q1 — le contrôle par doublement tranche donc toujours à lui seul.
 */
const verifierControleUnGeste = (couples, prop) => {
    if (couples[1][0] !== 2 * couples[0][0] || couples[2][0] !== 2 * couples[1][0]) {
        throw new Error(`gen_1084 : quantités ${couples.map((c) => c[0])} — chaîne de doublements rompue`);
    }
    const premierDouble = couples[1][1] === 2 * couples[0][1];
    if (premierDouble !== prop) {
        throw new Error('gen_1084 : le contrôle par doublement ne tranche pas ce tirage');
    }
    if (!prop && Math.abs(couples[1][1] - 2 * couples[0][1]) < 100) {
        throw new Error('gen_1084 : écart au double < 1,00 € — signal trop mince pour un rituel');
    }
    if (prop && couples[2][1] !== 2 * couples[1][1]) {
        throw new Error('gen_1084 : cas proportionnel dont le second doublement ne se confirme pas');
    }
    return couples;
};

const OUI_JUSTE = 'Oui : le prix pour un seul est le même quelle que soit la quantité.';
const NON_JUSTE = 'Non : le prix pour un seul change selon la quantité.';
// Misconceptions P2/P11 — mêmes gabarits, JAMAIS correctes.
const OUI_PIEGE = 'Oui : le prix augmente quand la quantité augmente.';
const NON_PIEGE = 'Non : les prix ne sont pas des nombres entiers.';

// Anti-répétition immédiate (recette standard).
let derniereSignatureTableau1084 = null;

const genTableauQcm = () => {
    let payload = construireTableauQcm();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureTableau1084; t++) {
        payload = construireTableauQcm();
    }
    derniereSignatureTableau1084 = payload.texte;
    return payload;
};

const construireTableauQcm = () => {
    const prop = Math.random() < 0.5;
    const article = randomChoice(ARTICLES_1084);
    const couples = verifierControleUnGeste(construireCouples(prop), prop);

    const donnees = couples
        .map(([q, c], i) => `${i === 0 ? `Pour ${q} ${article.plu}` : `pour ${q}`}, on paie ${eur(c)} €`)
        .join(' ; ');
    const texte = `<p>${donnees}.</p><p>Le prix payé est-il proportionnel au nombre de ${article.plu} ?</p>`;

    const bonne = prop ? OUI_JUSTE : NON_JUSTE;
    const payload = makeQcm([bonne, prop ? NON_JUSTE : OUI_JUSTE, OUI_PIEGE, NON_PIEGE], bonne, texte, opts(L_TABLEAU));
    payload._meta.params = { prop, couples };
    return payload;
};

// ─── Format 3 : QCM repérer (+ variante intrus « N'EST PAS ») ──────────────

// Anti-répétition immédiate (recette standard).
let derniereSignatureReperer1084 = null;

const genRepererQcm = () => {
    let payload = construireRepererQcm();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureReperer1084; t++) {
        payload = construireRepererQcm();
    }
    derniereSignatureReperer1084 = payload.texte;
    return payload;
};

const construireRepererQcm = () => {
    const intrus = Math.random() < 0.5;
    const famillesVues = new Set();
    // Direct : 1 proportionnelle parmi 3 non — intrus : l'inverse.
    const cible = tirerSituation(intrus ? SITUATIONS_NON_PROP : SITUATIONS_PROP, famillesVues);
    const autres = [];
    for (let k = 0; k < 3; k++) {
        const s = tirerSituation(intrus ? SITUATIONS_PROP : SITUATIONS_NON_PROP, famillesVues);
        if (s) autres.push(s);
    }
    if (!cible || autres.length < 3) throw new Error('gen_1084 : vivier insuffisant pour le QCM repérer');

    // Recomptage : exactement UN candidat au statut demandé.
    const tous = [cible, ...autres];
    const nbCibles = tous.filter((s) => (intrus ? !estProportionnelle(s.fam) : estProportionnelle(s.fam))).length;
    if (nbCibles !== 1) throw new Error(`gen_1084 : ${nbCibles} candidats au statut demandé (attendu 1)`);

    const texte = intrus
        ? '<p>Parmi ces situations, laquelle N\'EST PAS une situation de proportionnalité ?</p>'
        : '<p>Parmi ces situations, laquelle est une situation de proportionnalité ?</p>';

    const payload = makeQcm(tous.map((s) => s.texte), cible.texte, texte, opts(L_REPERER));
    if (intrus) payload.contenu = { ...payload.contenu, negated: true };
    payload._meta.params = { intrus, familles: tous.map((s) => s.fam) };
    return payload;
};

/** Vérité par famille — table unique, partagée par le recomptage. */
const estProportionnelle = (fam) => SITUATIONS_PROP.some((s) => s.fam === fam);

// ─── Format 4 : V/F logique (le modèle lui-même) ───────────────────────────

/**
 * Pool FIXE à vérité fixe : chaque ligne énonce une propriété du MODÈLE.
 * Les fausses matérialisent les misconceptions documentées (P11) : la
 * croissance simultanée, le modèle additif, la partie fixe.
 */
const POOL_LOGIQUE = [
    { l: 'Dans une situation de proportionnalité, à 0 correspond toujours 0.', v: true },
    { l: 'Dans une situation de proportionnalité, si on double une grandeur, l\'autre double aussi.', v: true },
    { l: 'Dans une situation de proportionnalité, si on triple une grandeur, l\'autre triple aussi.', v: true },
    // Formulation par le COEFFICIENT (revue 2026-09-01, A1) : « le quotient
    // est le même pour toutes les valeurs » butait sur 0 ÷ 0 — la version
    // multiplicative est vraie sans exception (0 × k = 0 compris).
    { l: 'Dans une situation de proportionnalité, pour passer d\'une grandeur à l\'autre, on multiplie toujours par le même nombre.', v: true },
    { l: 'Si deux grandeurs augmentent en même temps, alors elles sont proportionnelles.', v: false },
    // Instance CONCRÈTE (revue 2026-09-01, A1) : l'énoncé générique « ajouter
    // 10 aux deux » devient VRAI dans toute situation de coefficient 1 — un
    // contre-exemple concret rend le verdict incontestable (+1 kg ≠ +1 €).
    { l: 'Si 2 kg de pommes coûtent 5 €, alors 3 kg coûtent 6 €.', v: false },
    { l: 'Un prix comportant une partie fixe est proportionnel à la quantité achetée.', v: false },
    { l: 'Deux grandeurs proportionnelles prennent toujours des valeurs entières.', v: false },
    // Élargissement (revue 2026-09-01) : pool porté à 6 V / 6 F pour interdire
    // STRICTEMENT toute reprise d'une ligne au tirage suivant.
    { l: 'Dans une situation de proportionnalité, si une grandeur est divisée par 2, l\'autre est divisée par 2 aussi.', v: true },
    { l: 'Le périmètre d\'un carré est proportionnel à la longueur de son côté.', v: true },
    { l: 'L\'aire d\'un carré est proportionnelle à la longueur de son côté.', v: false },
    { l: 'Si 4 stylos identiques coûtent 6 €, alors 8 de ces stylos coûtent 14 €.', v: false },
];

// INTERDICTION STRICTE de reprise (revue 2026-09-01, décision prof) : aucune
// ligne du tirage précédent ne ressort à l'actualisation suivante — pool de
// 12 (6 V / 6 F), la mixité reste toujours atteignable après exclusion.
let derniersServisVf1084 = [];

const genLogiqueVf = () => {
    let tirage = null;
    for (let essai = 0; essai < 60 && !tirage; essai++) {
        const dispo = POOL_LOGIQUE.filter((it) => !derniersServisVf1084.includes(it.l));
        const cand = shuffle(dispo).slice(0, 4);
        const nbFaux = cand.filter((it) => !it.v).length;
        if (nbFaux >= 1 && nbFaux <= 3) tirage = cand;
    }
    if (!tirage) throw new Error('gen_1084 : tirage V/F logique sans mixité après 60 essais');
    derniersServisVf1084 = tirage.map((it) => it.l);

    const payload = makeVraiFaux(
        tirage.map((it) => it.l),
        tirage.map((it) => (it.v ? 0 : 1)),
        '<p>Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
        opts(L_LOGIQUE),
    );
    // Métadonnée DORMANTE (convention gen_1039) : cadence de raisonnement.
    payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
    return payload;
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    situations_tableau_vf: genSituationsVf,
    situations_classer: genSituationsClasser,
    tableau_qcm_unique: genTableauQcm,
    reperer_qcm_unique: genRepererQcm,
    logique_tableau_vf: genLogiqueVf,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux', classer: 'Classer',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    {
        // Fusion revue 2026-09-01 : le V/F, le classer et le QCM repérer puisent
        // dans les MÊMES viviers de situations et évaluent le même jugement —
        // trois interactions d'une seule tâche, une seule carte.
        id: 'situations',
        label: L_SITUATIONS,
        formatsList: [...buildFormats('situations'), ...buildFormats('reperer')],
    },
    { id: 'tableau', label: L_TABLEAU, formatsList: buildFormats('tableau') },
    { id: 'logique', label: L_LOGIQUE, formatsList: buildFormats('logique') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
